import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  Button,
  Container,
  Heading,
  Icon,
  majorScale,
  Menu,
  MenuDivider,
  MenuItem,
  PageContainer,
  PageHeader,
  Popover,
  Section,
  Spinner,
  Table,
  useAlert,
  useToast,
} from '../../npaper';
import { useStores } from '../../stores';
import { ILocation } from '../../stores/warehousing/LocationStore';
import { IWarehouse } from '../../stores/warehousing/WarehouseStore';
import LocationDetailsDialog from './LocationDetailsDialog';
import ProductEntryDialog from './ProductEntriesDialog';
import WarehouseDetailsDialog from './WarehouseDetailsDialog';

const Warehousing = observer(() => {
  const intl = useIntl();
  const taoster = useToast();

  const { warehouseStore } = useStores();

  useEffect(() => {
    warehouseStore.getAll().catch(taoster.showDefaultError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseStore]);

  const [isCreatingWarehouse, setIsCreatingWarehouse] = useState(false);

  return (
    <PageContainer>
      <WarehouseDetailsDialog
        isShown={isCreatingWarehouse}
        onClose={() => {
          setIsCreatingWarehouse(false);
        }}
      />
      <Container>
        <PageHeader
          title={intl.formatMessage({ defaultMessage: 'Warehousing' })}
          actions={
            <Button iconLeft="add" onClick={() => setIsCreatingWarehouse(true)}>
              <FormattedMessage defaultMessage="Create warehouse" />
            </Button>
          }
        />
        {warehouseStore.isLoading ? (
          <Spinner />
        ) : warehouseStore.items.length === 0 ? (
          <Box width="100%" textAlign="center" paddingY={majorScale(6)}>
            <Icon
              icon="box"
              size={majorScale(8)}
              marginBottom={majorScale(2)}
            />
            <Heading size={500} marginBottom={majorScale(1)}>
              <FormattedMessage defaultMessage="No warehouses defined yet" />
            </Heading>
            <p>
              <FormattedMessage defaultMessage="Create a new warehouse to start managing stock" />
              <Button primary marginX="auto" marginTop={majorScale(2)}>
                <FormattedMessage defaultMessage="Create new warehouse" />
              </Button>
            </p>
          </Box>
        ) : (
          warehouseStore.items.map((warehouse, i) => (
            <WarehouseSection warehouse={warehouse} key={warehouse.id} />
          ))
        )}
      </Container>
    </PageContainer>
  );
});

export default Warehousing;

const WarehouseSection = observer(
  ({ warehouse }: { warehouse: IWarehouse }) => {
    const toaster = useToast();
    const intl = useIntl();
    const deleteAlert = useAlert({
      message: '',
      intent: 'danger',
      confirmButtonText: intl.formatMessage({
        defaultMessage: 'Delete location',
      }),
      icon: 'trash',
    });
    const { locationStore } = useStores();

    const [editWarehouse, setEditWarehouse] = useState(false);
    const [locations, setLocations] = useState<ILocation[]>();

    useEffect(() => {
      let isSub = true;
      locationStore
        .getAll({
          filter: {
            warehouse: warehouse.id,
          },
        })
        .then((cache) => isSub && setLocations(cache.items));
      return () => {
        isSub = false;
      };
    }, [locationStore, warehouse]);

    const [isCreatingLocation, setIsCreatingLocation] = useState(false);
    const [editLocation, setEditLocation] = useState<ILocation | null>(null);
    const [inspectEntriesForLocation, setInspectEntriesForLocation] =
      useState<ILocation | null>(null);

    return (
      <Section
        title={warehouse.name}
        actions={
          <Popover
            content={(close) => (
              <Menu>
                <MenuItem
                  text={<FormattedMessage defaultMessage="Edit warehouse" />}
                  icon="edit"
                  onClick={() => {
                    close();
                    setEditWarehouse(true);
                  }}
                />
                <MenuItem
                  icon="add"
                  text={intl.formatMessage({
                    defaultMessage: 'Create new location',
                  })}
                  onClick={() => {
                    close();
                    setIsCreatingLocation(true);
                  }}
                />
                <MenuDivider />
                <MenuItem
                  icon="trash"
                  intent="danger"
                  text={<FormattedMessage defaultMessage="Delete warehouse" />}
                  onClick={() => {
                    close();
                  }}
                />
              </Menu>
            )}
          >
            <Button iconRight="chevron-down">
              <FormattedMessage defaultMessage="Actions" />
            </Button>
          </Popover>
        }
      >
        <LocationDetailsDialog
          isShown={isCreatingLocation}
          onClose={() => setIsCreatingLocation(false)}
          warehouse={warehouse}
        />
        <WarehouseDetailsDialog
          isShown={!!editWarehouse}
          onClose={() => {
            setEditWarehouse(false);
          }}
          warehouse={warehouse}
        />
        <LocationDetailsDialog
          isShown={!!editLocation}
          onClose={() => setEditLocation(null)}
          location={editLocation || undefined}
        />
        {!locations ? (
          <Spinner size={16} />
        ) : locations.length === 0 ? (
          <Box className="p">
            <FormattedMessage defaultMessage="No locations defined for warehouse" />
          </Box>
        ) : (
          <Box
            maxHeight="80vh"
            overflow="auto"
            position="relative"
            marginTop={majorScale(-2)}
          >
            <Table>
              <thead>
                <tr>
                  <th>
                    <FormattedMessage defaultMessage="Code" />
                  </th>
                  <th>
                    <FormattedMessage defaultMessage="Description" />
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => (
                  <tr key={location.id}>
                    <ProductEntryDialog
                      isShown={inspectEntriesForLocation === location}
                      filter={{
                        location: location.id,
                      }}
                      onClose={() => setInspectEntriesForLocation(null)}
                    />
                    <td className="font-mono medium">{location.code}</td>
                    <td>{location.description}</td>
                    <td>
                      <Popover
                        content={(close) => (
                          <Menu>
                            <MenuItem
                              icon="edit"
                              text={intl.formatMessage({
                                defaultMessage: 'Edit',
                              })}
                              onClick={() => {
                                close();
                                setEditLocation(location);
                              }}
                            />
                            <MenuItem
                              icon="th"
                              text={intl.formatMessage({
                                defaultMessage: 'Show entries',
                              })}
                              onClick={() => {
                                close();
                                setInspectEntriesForLocation(location);
                              }}
                            />
                            <MenuDivider />
                            <MenuItem
                              icon="trash"
                              intent="danger"
                              text={intl.formatMessage({
                                defaultMessage: 'Delete',
                              })}
                              onClick={() => {
                                close();
                                deleteAlert.show(
                                  async () => {
                                    locationStore
                                      .deleteItem(location)
                                      .catch(toaster.showDefaultError);
                                  },
                                  async () => {},
                                  {
                                    title: intl.formatMessage({
                                      defaultMessage: 'Deleting location',
                                    }),
                                    message: intl.formatMessage(
                                      {
                                        defaultMessage:
                                          'Are you sure you want to delete {code}?',
                                      },
                                      { code: location.code }
                                    ),
                                  }
                                );
                              }}
                            />
                          </Menu>
                        )}
                      >
                        <Button marginLeft="auto" minimal iconLeft="more" />
                      </Popover>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}
      </Section>
    );
  }
);
